import { useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Braces } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface InsertOption {
  /** So wie es im Text steht, etwa `{{Apartment}}`. */
  token: string;
  label: string;
  /** Kurz rechts daneben: ein Beispielwert oder wann der Wert erscheint. */
  example: string;
  /** Eine Zeile darunter, wo der Name allein nicht reicht. */
  detail?: string;
  /** Warum das Feld hier nicht geht — dann bleibt der Eintrag sichtbar, aber gesperrt. */
  blocked?: { short: string; why: string };
}

/**
 * „Feld einfügen“ für ein Textfeld: erlaubte Felder oben, gesperrte mit Grund darunter. Ein Menü
 * statt eines Popovers, damit es auch in Sheets und Dialogen bedienbar bleibt (§8).
 */
export function InsertFieldMenu({
  fieldLabel,
  options,
  open,
  onOpenChange,
  onInsert,
  onCloseFocus,
}: {
  fieldLabel: string;
  options: InsertOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (token: string) => void;
  /** Der Fokus gehört danach zurück ins Textfeld, nicht auf den Knopf. */
  onCloseFocus: () => void;
}) {
  // Wer daneben klickt, will dorthin — dann holt das Menü den Fokus nicht ins Feld zurück.
  const outside = useRef(false);
  const allowed = options.filter((option) => !option.blocked);
  const blocked = options.filter((option) => option.blocked);
  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Feld in ${fieldLabel} einfügen`}
          className="border-line text-ink hover:border-sage focus-visible:outline-sage inline-flex min-h-11 items-center gap-1.5 rounded-md border bg-white px-3 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <Braces size={14} aria-hidden />
          Feld einfügen
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          onInteractOutside={() => {
            outside.current = true;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!outside.current) onCloseFocus();
            outside.current = false;
          }}
          className="border-line/60 bg-card shadow-card z-50 w-[min(22rem,calc(100vw-2rem))] rounded-lg border p-1.5"
        >
          <DropdownMenu.Label className="text-ink-soft px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]">
            In {fieldLabel === 'Text' ? 'den Text' : `„${fieldLabel}“`} einfügen
          </DropdownMenu.Label>
          {allowed.map((option) => (
            <Item key={option.token} option={option} onSelect={() => onInsert(option.token)} />
          ))}
          {blocked.length > 0 && <DropdownMenu.Separator className="bg-line/60 my-1 h-px" />}
          {blocked.map((option) => (
            <Item key={option.token} option={option} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Item({ option, onSelect }: { option: InsertOption; onSelect?: () => void }) {
  const below = option.blocked ? option.blocked.why : option.detail;
  return (
    <DropdownMenu.Item
      disabled={!!option.blocked}
      onSelect={onSelect}
      className={cn(
        'grid min-h-11 cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-md px-2.5 py-1.5 text-sm outline-none',
        option.blocked
          ? 'text-ink-faint cursor-not-allowed'
          : 'text-ink data-[highlighted]:bg-card-alt',
      )}
    >
      <span className="font-medium">{option.label}</span>
      <span className="text-ink-soft truncate text-xs">
        {option.blocked ? option.blocked.short : option.example}
      </span>
      {below && <span className="text-ink-soft col-span-2 text-xs leading-snug">{below}</span>}
    </DropdownMenu.Item>
  );
}
